import { useContext, useEffect, useState } from 'react';

import AccountPickerContext from 'contexts/AccountPickerContext';
import STORE from 'store';

// Raw `tags` values an SE has defined for the given dataType(s) but which may
// not yet be present on any record. Lets a keyword filter offer a not-yet-
// applied tag as a selectable value (see KeywordPicker), so a metric can be
// built against a tag before it has been applied to data.
//
// Returns the raw ALL-CAPS tag strings (what gets written to records), NOT the
// display labels. The hardcoded EXCLUDED tag is intentionally excluded — only
// SE-defined tags are surfaced.
const useAcceptableTagValues = ({ dataTypes }: { dataTypes: string[] }) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const [tagValues, setTagValues] = useState<string[]>([]);

  // Join to a stable primitive so the effect doesn't re-run on every render
  // (dataTypes is a fresh array each time).
  const dataTypesKey = dataTypes.join('|');

  useEffect(() => {
    const types = dataTypesKey ? dataTypesKey.split('|') : [];
    if (types.length === 0) {
      setTagValues([]);
      return;
    }

    let isActive = true;
    STORE.bonus
      .getBonusTagsRef({ accountId: selectedAccountId })
      .get()
      .then((snapshot) => {
        if (!isActive) return;
        const values = snapshot.docs
          .map((doc) => doc.data())
          .filter((tag) => types.includes(tag.dataType))
          .map((tag) => tag.tag);
        setTagValues(values);
      })
      .catch(() => {
        if (isActive) setTagValues([]);
      });

    return () => {
      isActive = false;
    };
  }, [dataTypesKey, selectedAccountId]);

  return tagValues;
};

export default useAcceptableTagValues;
